const pad = (value: number): string => value.toString().padStart(2, "0");

export const formatDateInput = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

export const formatTimeInput = (date: Date = new Date()): string => {
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${hours}:${minutes}`;
};

const ISO_WITH_TZ = /[zZ]$|[+-]\d{2}:?\d{2}$/;

const parseToDate = (value: string): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Treat naked YYYY-MM-DDTHH:mm[:ss] (no timezone) as local wall time.
  const naked = trimmed.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/,
  );
  if (naked && !ISO_WITH_TZ.test(trimmed)) {
    const [, ymd, hh, mm, ss = "0", ms = "0"] = naked;
    const [y, mo, d] = ymd.split("-").map(Number);
    return new Date(
      y,
      mo - 1,
      d,
      Number(hh),
      Number(mm),
      Number(ss),
      Number(ms.padEnd(3, "0").slice(0, 3)),
    );
  }
  const direct = new Date(trimmed);
  return Number.isFinite(direct.getTime()) ? direct : null;
};

/**
 * Convert a local YYYY-MM-DD + HH:mm pair to a UTC ISO instant.
 * Falls back to "now" when both inputs are blank.
 */
export const toUtcIsoFromLocalDateTime = (date: string, time: string): string => {
  const trimmedDate = date.trim();
  const trimmedTime = time.trim();
  if (!trimmedDate && !trimmedTime) {
    return new Date().toISOString();
  }
  const baseDate = trimmedDate || formatDateInput();
  const baseTime = trimmedTime || "00:00";
  const dateObj = parseToDate(`${baseDate}T${baseTime}`);
  if (!dateObj) return new Date().toISOString();
  return dateObj.toISOString();
};

/**
 * Build a logged-at instant. Inputs are local wall-clock; output is UTC ISO.
 * Replaces the legacy string-concat behavior.
 */
export const buildLoggedAt = (date: string, time: string): string =>
  toUtcIsoFromLocalDateTime(date, time);

/** Render the local YYYY-MM-DD calendar date for an instant. */
export const toLocalDateInput = (utcIso: string): string => {
  const dateObj = parseToDate(utcIso);
  if (!dateObj) return "";
  return formatDateInput(dateObj);
};

/** Render the local HH:mm time-of-day for an instant. */
export const toLocalTimeInput = (utcIso: string): string => {
  const dateObj = parseToDate(utcIso);
  if (!dateObj) return "";
  return formatTimeInput(dateObj);
};

/**
 * Format an instant for display in the user's locale (e.g. "8:30 AM").
 * Never expose UTC strings to the user.
 */
export const formatLocalTime = (
  utcIso: string,
  options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" },
): string => {
  const dateObj = parseToDate(utcIso);
  if (!dateObj) return "";
  return dateObj.toLocaleTimeString(undefined, options);
};

/**
 * Local calendar date for an instant. Diary day grouping should use this so
 * a 11pm log doesn't roll over a UTC date boundary.
 */
export const getLoggedDate = (loggedAt: string): string => {
  const local = toLocalDateInput(loggedAt);
  if (local) return local;
  return loggedAt.split("T")[0] ?? "";
};
